import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import {
    createDistributorContextProvider,
    getBreakpointRange,
    PlatformImageAssetLoaderContext,
    ResponsiveBreakpointProvider,
} from '@mews-distributor/common';
import { createRepositoryRegistryProvider } from '@mews-distributor/data-access';
import {
    createAssetLoaderProvider,
    createFeatureFlagServiceProvider,
    getIconsetStyles,
    ModuleManagerContextProvider,
    ReactQueryDevtools,
    RecaptchaServiceContextProvider,
    reportMissingIcons,
    StrictModeWrapper,
    WindowSizeProvider,
} from '@mews/framework';
import { BreakpointRangeProvider } from '@optimus-web/core';
import FontProvider from '@optimus-web/fonts';
import { App } from './components/App';
import { DocumentTitle } from './components/service/documentTitle';
import { IntlLocalizationProvider } from './components/service/IntlLocalizationProvider';
import { LocalizationProvider } from './components/service/localizationProvider';
import { RepositoryContext } from './components/service/RepositoryContext';
import { ThemeProvider } from './components/service/themeProvider';
import { getContentUrl } from './modules/configuration/selectors';

export const renderApp = ({
    store,
    contextWindow,
    localizationHandlers,
    platformImageAssetLoader,
    element,
    appClockService,
    calendarService,
    featureFlagService,
    moduleManager,
    loggerService,
    apolloClient,
    reactQueryClient,
    repositories,
    gainsightService,
    recaptchaService,
}: $TSFixMe) => {
    const { getState } = store;
    const baseUrl = getContentUrl(getState());
    const IconsetGlobalStyles = getIconsetStyles(baseUrl);

    const PlatformImageAssetLoaderProvider = createAssetLoaderProvider(
        platformImageAssetLoader,
        PlatformImageAssetLoaderContext
    );
    const FeatureFlagProvider = createFeatureFlagServiceProvider(featureFlagService);

    const DistributorContextProvider = createDistributorContextProvider({
        loggerService,
        appClockService,
        getCalendarService: () => calendarService,
        gainsightService,
    });

    const RepositoryRegistryProvider = createRepositoryRegistryProvider<{ children: React.ReactNode }>(repositories);

    const missingIconsErrorHandler = (error: $TSFixMe) => {
        loggerService.error('icons-not-loaded', 'Mews Icons font not loaded', {
            message: error.message,
            error,
        });
    };
    reportMissingIcons(missingIconsErrorHandler);

    const enableStrictMode = __REACT_STRICT_MODE__;

    try {
        ReactDOM.render(
            <StrictModeWrapper enableStrictMode={enableStrictMode}>
                <Provider store={store}>
                    <FeatureFlagProvider>
                        <FontProvider />
                        <DistributorContextProvider>
                            <ModuleManagerContextProvider value={moduleManager}>
                                <IconsetGlobalStyles />
                                <IntlLocalizationProvider>
                                    <RecaptchaServiceContextProvider value={recaptchaService}>
                                        {/* @ts-expect-error $TSFixMeError: Type '{ children: Element; localizationHandlers: any; }' is not assignable to type 'IntrinsicAttributes & (Omit<unknown,... */}
                                        <LocalizationProvider localizationHandlers={localizationHandlers}>
                                            <ThemeProvider>
                                                <WindowSizeProvider>
                                                    <BreakpointRangeProvider getBreakpointRange={getBreakpointRange}>
                                                        <ResponsiveBreakpointProvider>
                                                            <PlatformImageAssetLoaderProvider>
                                                                {/* @ts-expect-error $TSFixMeError: Type '{ contextWindow: any; }' is not assignable to type 'IntrinsicAttributes & Omit<{}, never>'. */}
                                                                <DocumentTitle contextWindow={contextWindow} />
                                                                <RepositoryContext.Provider value={repositories}>
                                                                    <RepositoryRegistryProvider>
                                                                        <QueryClientProvider client={reactQueryClient}>
                                                                            {apolloClient != null ? (
                                                                                <ApolloProvider client={apolloClient}>
                                                                                    <App />
                                                                                </ApolloProvider>
                                                                            ) : (
                                                                                <App />
                                                                            )}
                                                                            <ReactQueryDevtools
                                                                                initialVisibility={false}
                                                                            />
                                                                        </QueryClientProvider>
                                                                    </RepositoryRegistryProvider>
                                                                </RepositoryContext.Provider>
                                                            </PlatformImageAssetLoaderProvider>
                                                        </ResponsiveBreakpointProvider>
                                                    </BreakpointRangeProvider>
                                                </WindowSizeProvider>
                                            </ThemeProvider>
                                        </LocalizationProvider>
                                    </RecaptchaServiceContextProvider>
                                </IntlLocalizationProvider>
                            </ModuleManagerContextProvider>
                        </DistributorContextProvider>
                    </FeatureFlagProvider>
                </Provider>
            </StrictModeWrapper>,
            document.querySelector(element)
        );
    } catch (error) {
        let htmlElement = null;

        /**
         * Done to fix a Cyclic structure error that happens when you'd try to serialize a cyclic HTML element in a loggerService.
         * Because we're not sure if outerHtml will throw the same error, we're wrapping it in a try-catch block.
         */
        try {
            htmlElement = document.querySelector(element)?.outerHTML?.slice(0, 250);
        } catch (e) {
            loggerService.error('react-app-render-error-html-element', 'Failed to serialize HTML element.', {
                error: e,
            });
        }

        loggerService.log('react-app-render-error', 'React app render failed.', {
            error,
            extra: {
                element,
                htmlElement,
            },
        });
    }
};
